import React, { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useApi } from './api.context';
import { useJamChallenge } from './jam-challenge.context';
import { useJamEventDetails } from './jam-event-details.context';
import { useChallengeProgress } from '@/src/hooks/useChallengeProgress';
import {
  ChallengeLabStatus,
  JamArenaSubmitResult,
  JamArenaVerdict,
  JamArenaWriteUp,
  JamArenaWriteUpState,
  JamChallengeDetails,
  JamProgress,
  JamTask,
  JamTaskClue,
  SolveChallengeButtonKey,
} from '../types/JamChallengeDetails';
import { TaskValidationResponse } from '../types/JamChallenges';
import { AwsAccount, TeamChallengePropertiesResponse } from '../types/JamFacilitator';
import { KeyValuePair } from '../types/KeyValuePair';
import { getErrorCode, getErrorMessage } from '../utils/errors.utils';
import { safeString } from '../utils/string.utils';
import { noop } from 'lodash';
import { JamEventSupportDetails } from '../types/JamChallengeSupportDetails';
import { TeamChallengeQueueInfo } from '../types/TeamChallengeQueueInfo';
import localStorageTTL from '@/src/utils/localStorageTTL.utils';

export interface JamChallengeDetailsContextValue {
  isFetchingJamChallengeDetails: boolean;
  loadJamChallengeDetails: (params: { id: string; challengeId: string }) => Promise<JamChallengeDetails>;
  loadJamChallengeTaskClue: (
    params: { id: string; challengeId: string },
    taskId: string,
    clueOrder: number
  ) => Promise<JamTaskClue>;
  selectedJamChallenge: JamChallengeDetails | undefined;
  /** True when challengeProgress belongs to the currently-selected challenge. */
  progressMatchesSelectedChallenge: boolean;
  /** Progress object ONLY when it matches the selected challenge, else undefined. */
  matchedChallengeProgress: JamProgress | undefined;
  /** Convenience: matched AND started (mirrors the existing isStarted pattern). */
  isSelectedChallengeStarted: boolean;
  startJamChallenge: (params: { id: string; challengeId: string }) => Promise<void>;
  reStartJamChallenge: (params: { id: string; challengeId: string; reason: string }) => Promise<void>;
  loadJamChallengeProgressData: (params: { id: string; challengeId: string }) => Promise<JamProgress>;
  validateTaskAnswer: (
    params: { id: string; challengeId: string },
    answer: string,
    taskId: string | undefined
  ) => Promise<boolean>;
  challengeProgress: JamProgress | undefined;
  /** Per-challenge refresh counter, bumped on a progress/lab-assignment websocket. Consumers watch
   * it and re-fetch /progress; the payload is a trigger, not data (its locks can be stale). */
  challengeRefreshTicks: { [challengeId: string]: number };
  /** Authoritative `/progress` for every challenge in the event, keyed by challenge id. */
  progressById: { [challengeId: string]: JamProgress };
  /** Lab status for every challenge in the event, keyed by challenge id. */
  challengeStatuses: { [challengeId: string]: ChallengeLabStatus };
  task: JamTask | undefined;
  awsAccount: AwsAccount | undefined;
  awsAccountProperties: KeyValuePair[];
  outputProperties: KeyValuePair[] | undefined;
  getJamChallengeTask: (params: { taskId: string }) => void;
  taskValidationResponse: TaskValidationResponse | undefined;
  loadTeamProperties: (eventName: string, challengeId: string) => Promise<void>;
  teamPropertiesResponse: TeamChallengePropertiesResponse | undefined;
  trackSolveWithButtonClick: (
    eventName: string,
    challengeId: string,
    buttonKey: SolveChallengeButtonKey
  ) => Promise<void>;
  loadTeamKeyPair: (eventName: string, challengeId: string) => Promise<void>;
  jamChallengeId: string;
  isStartingChallange: boolean;
  showChallengeStatusProgress: boolean;
  showChallengeStatusSuccess: boolean;
  enableChallengeStatusModal: boolean;
  setEnableChallengeStatusModalFunc: () => void;
  isFetchingTaskClue: boolean;
  fetchingtaskValidationResponse: boolean;
  loadChallengeSupportDetails: (eventName: string, challengeId: string) => Promise<void>;
  challengeSupportDetails?: JamEventSupportDetails;
  loadJamChallengeQueueInfo: (eventName: string, challengeId: string) => Promise<void>;
  queueInfo?: TeamChallengeQueueInfo;
  challengeModalMap?: { [id: string]: boolean };
  handleWSChallengeProgressUpdate: (o: JamProgress) => Promise<void> | void;
  handleWSLabAssignmentUpdate: (messageBody: { eventName?: string; challengeId?: string }) => void;
  handleWSLabAvailabilityUpdate: (messageBody: {
    eventName?: string;
    labAvailability?: { [challengeId: string]: boolean };
  }) => void;
  /** Arena (SCENARIO challenges): latest write-up save state, submit result, and verdict. */
  arenaWriteUpState: JamArenaWriteUpState | undefined;
  arenaSubmitResult: JamArenaSubmitResult | undefined;
  arenaVerdict: JamArenaVerdict | undefined;
  isSavingArenaWriteUp: boolean;
  isSubmittingArena: boolean;
  saveArenaWriteUp: (eventName: string, challengeId: string, writeUp: JamArenaWriteUp) => Promise<void>;
  submitArena: (eventName: string, challengeId: string) => Promise<void>;
  loadArenaVerdict: (eventName: string, challengeId: string) => Promise<void>;
}

const JamChallengeDetailsContext = React.createContext<JamChallengeDetailsContextValue>({
  isFetchingJamChallengeDetails: true,
  loadJamChallengeDetails: () => new Promise(noop),
  loadJamChallengeTaskClue: () => new Promise(noop),
  selectedJamChallenge: undefined,
  progressMatchesSelectedChallenge: false,
  matchedChallengeProgress: undefined,
  isSelectedChallengeStarted: false,
  startJamChallenge: () => new Promise(noop),
  reStartJamChallenge: () => new Promise(noop),
  loadJamChallengeProgressData: () => new Promise(noop),
  validateTaskAnswer: () => new Promise(noop),
  challengeProgress: undefined,
  challengeRefreshTicks: {},
  progressById: {},
  challengeStatuses: {},
  task: undefined,
  awsAccount: undefined,
  awsAccountProperties: [],
  outputProperties: undefined,
  getJamChallengeTask: noop,
  taskValidationResponse: undefined,
  loadTeamProperties: () => new Promise(noop),
  teamPropertiesResponse: undefined,
  trackSolveWithButtonClick: () => new Promise(noop),
  loadTeamKeyPair: () => new Promise(noop),
  jamChallengeId: '',
  isStartingChallange: false,
  showChallengeStatusProgress: false,
  showChallengeStatusSuccess: false,
  enableChallengeStatusModal: false,
  setEnableChallengeStatusModalFunc: noop,
  isFetchingTaskClue: false,
  fetchingtaskValidationResponse: false,
  loadChallengeSupportDetails: () => new Promise(noop),
  challengeSupportDetails: undefined,
  loadJamChallengeQueueInfo: () => new Promise(noop),
  queueInfo: undefined,
  challengeModalMap: {},
  handleWSChallengeProgressUpdate: noop,
  handleWSLabAssignmentUpdate: noop,
  handleWSLabAvailabilityUpdate: noop,
  arenaWriteUpState: undefined,
  arenaSubmitResult: undefined,
  arenaVerdict: undefined,
  isSavingArenaWriteUp: false,
  isSubmittingArena: false,
  saveArenaWriteUp: () => new Promise(noop),
  submitArena: () => new Promise(noop),
  loadArenaVerdict: () => new Promise(noop),
});

const JamChallengeDetailsProvider: React.FC = ({ children }) => {
  const { jamChallengeDetailsApi, jamFacilitatorApi } = useApi();
  const [selectedJamChallenge, setSelectedJamChallenge] = useState<JamChallengeDetails>();
  const [selectedJamChallengeId, setSelectedJamChallengeId] = useState<string>();
  const [selectedJamEventId, setSelectedJamEventId] = useState<string>();
  const [challengeProgress, setChallengeProgress] = useState<JamProgress>();
  const [challengeRefreshTicks, setChallengeRefreshTicks] = useState<{ [challengeId: string]: number }>({});

  // Sets the selected-challenge progress slot. The sidebar instead reacts to refresh ticks.
  const recordChallengeProgress = (progress: JamProgress | undefined) => {
    setChallengeProgress(progress);
  };

  // Bump a per-challenge counter so trigger-based consumers re-fetch /progress. We bump rather than
  // forward the payload because its task-lock fields can be stale at challenge start.
  const bumpChallengeRefreshTick = (challengeId: string | undefined) => {
    if (!challengeId) {
      return;
    }
    setChallengeRefreshTicks((prev) => ({ ...prev, [challengeId]: (prev[challengeId] ?? 0) + 1 }));
  };

  const { jamChallengeData } = useJamChallenge();
  const { event: currentEvent, eventName: currentEventName } = useJamEventDetails();
  // Stable identity: a change resets the hook's per-event guards and retry chains.
  const fetchChallengeProgress = useCallback(
    (challengeId: string) => jamChallengeDetailsApi.fetchJamChallengeProgress(currentEventName, challengeId),
    [jamChallengeDetailsApi, currentEventName]
  );
  const { statuses: challengeStatuses, progressById } = useChallengeProgress({
    challenges: jamChallengeData?.challenges,
    challengeRefreshTicks,
    eventEnded: !!currentEvent?.ended,
    fetchProgress: fetchChallengeProgress,
  });
  const [fetchingtaskValidationResponse, setFetchingTaskValidationResponse] = useState(false);
  const [taskValidationResponse, setTaskValidationResponse] = useState<TaskValidationResponse>();
  const [teamPropertiesResponse, setTeamPropertiesResponse] = useState<TeamChallengePropertiesResponse>();
  const [isFetchingJamChallengeDetails, setIsFetchingJamChallenge] = useState(true);
  const [task, setTask] = useState<JamTask>();
  const [isFetchingTaskClue, setIsFetchingTaskClue] = useState(false);
  const [awsAccount, setAwsAccount] = useState<AwsAccount | undefined>(undefined);
  const [awsAccountProperties, setAwsAccountProperties] = useState<KeyValuePair[]>([]);
  const [outputProperties, setOutputProperties] = useState<KeyValuePair[]>();
  const [isStartingChallange, setIsStartingChallange] = useState(false);
  const [showChallengeStatusProgress, setShowChallengeStatusProgress] = useState<boolean>(false);
  const [showChallengeStatusSuccess, setShowChallengeStatusSuccess] = useState<boolean>(false);
  const [enableChallengeStatusModal, setEnableChallengeStatusModal] = useState<boolean>(false);
  const [challengeModalMap, setChallengeModalMap] = useState<{ [id: string]: boolean }>({});
  const [challengeSupportDetails, setChallengeSupportDetails] = useState<JamEventSupportDetails>();
  const [queueInfo, setQueueInfo] = useState<TeamChallengeQueueInfo>();
  const [arenaWriteUpState, setArenaWriteUpState] = useState<JamArenaWriteUpState>();
  const [arenaSubmitResult, setArenaSubmitResult] = useState<JamArenaSubmitResult>();
  const [arenaVerdict, setArenaVerdict] = useState<JamArenaVerdict>();
  const [isSavingArenaWriteUp, setIsSavingArenaWriteUp] = useState(false);
  const [isSubmittingArena, setIsSubmittingArena] = useState(false);
  const jamChallengeId = selectedJamChallenge?.id || '';

  /**
   * Mirrors of the selected challenge/event state.
   *
   * The start-challenge flow chains several async calls from a single event handler, so any
   * callback in that chain still sees the state snapshot from the render it was created in.
   * That is why the awaiting-lab re-fetch below must read these refs rather than the state
   * values directly - by the time it runs, the state values are a pre-start snapshot.
   */
  const selectedJamChallengeRef = useRef<JamChallengeDetails | undefined>(undefined);
  const selectedJamChallengeIdRef = useRef<string | undefined>(undefined);
  const selectedJamEventIdRef = useRef<string | undefined>(undefined);

  /** Keeps the ref and the state value in lockstep so async callers always read fresh data. */
  const updateSelectedJamChallenge = (challenge: JamChallengeDetails | undefined) => {
    selectedJamChallengeRef.current = challenge;
    setSelectedJamChallenge(challenge);
  };

  const updateSelectedChallengeKeys = (eventId: string, challengeId: string) => {
    selectedJamEventIdRef.current = eventId;
    selectedJamChallengeIdRef.current = challengeId;
    setSelectedJamEventId(eventId);
    setSelectedJamChallengeId(challengeId);
  };

  // Mirror of the selected task so async clue writes can gate on the task selected at RESOLVE
  // time (via the ref), not the one captured in their closure.
  const selectedTaskRef = useRef<JamTask | undefined>(undefined);

  const updateTask = (nextTask: JamTask | undefined) => {
    selectedTaskRef.current = nextTask;
    setTask(nextTask);
  };

  const resetChallengeState = () => {
    setOutputProperties(undefined);
    setEnableChallengeStatusModal(false);
    setAwsAccountProperties([]);
    setAwsAccount(undefined);
    updateSelectedJamChallenge(undefined);
    updateTask(undefined);
    setArenaWriteUpState(undefined);
    setArenaSubmitResult(undefined);
    setArenaVerdict(undefined);
  };

  /**
   * Silently re-fetch the currently selected challenge's detail and update state in place.
   *
   * Unlike loadJamChallengeDetails this does NOT reset challenge state (which would blank the
   * page/navigate away), so it is safe to call in response to websocket updates. It is used to
   * automatically unlock tasks once the team's lab account has been assigned.
   */
  const refetchSelectedChallengeDetail = async (): Promise<void> => {
    // Read from refs: callers in the start-challenge chain hold a stale render closure, where
    // these ids can still be undefined even though the challenge is already selected.
    const eventId = selectedJamEventIdRef.current;
    const challengeId = selectedJamChallengeIdRef.current;
    if (!eventId || !challengeId) {
      return;
    }
    try {
      const response = await jamChallengeDetailsApi.fetchJamChallengeDetailsData(eventId, challengeId);
      localStorageTTL.setItem(`challenge-details-${eventId}-${challengeId}`, response, 1, 'hours');
      updateSelectedJamChallenge(response);
      const accountReady = !!response.awsLabAccount && !response.awaitingLabAssignment;
      toggleChallengeStatusModal(!accountReady, accountReady, challengeId);
    } catch (e) {
      // Best-effort refresh; the user can still reload via normal navigation.
    }
  };

  // Lab just got assigned to the selected challenge: unlock tasks and load lab credentials in place.
  const refreshSelectedChallengeLab = () => {
    void refetchSelectedChallengeDetail();
    const eventId = selectedJamEventIdRef.current;
    const challengeId = selectedJamChallengeIdRef.current;
    if (eventId && challengeId) {
      // Best-effort; loadTeamProperties rethrows after handling known error codes.
      void loadTeamProperties(eventId, challengeId).catch(noop);
    }
  };

  // True when an async response's event/challenge no longer matches the current selection.
  const isStaleSelection = (eventName: string, challengeId: string) =>
    (selectedJamChallengeIdRef.current !== undefined && challengeId !== selectedJamChallengeIdRef.current) ||
    (selectedJamEventIdRef.current !== undefined && eventName !== selectedJamEventIdRef.current);

  const saveArenaWriteUp = async (eventName: string, challengeId: string, writeUp: JamArenaWriteUp) => {
    setIsSavingArenaWriteUp(true);
    try {
      // Echo the version we last saw: the last save's, else the one loadChallenge delivered.
      const version = arenaWriteUpState?.version ?? selectedJamChallengeRef.current?.arena?.team?.version;
      const response = await jamChallengeDetailsApi.saveArenaWriteUp(eventName, challengeId, writeUp, version);
      if (isStaleSelection(eventName, challengeId)) {
        return;
      }
      setArenaWriteUpState(response);
    } catch (e: any) {
      // A stale version (teammate saved first) comes back as 409. ApiClient drops the body, so
      // recover by re-fetching the challenge: the panel re-seeds from arena.team.
      if ((getErrorCode(e) === 409 || /409/.test(getErrorMessage(e))) && !isStaleSelection(eventName, challengeId)) {
        setArenaWriteUpState(undefined);
        await refetchSelectedChallengeDetail();
      }
      throw e;
    } finally {
      setIsSavingArenaWriteUp(false);
    }
  };

  const submitArena = async (eventName: string, challengeId: string) => {
    setIsSubmittingArena(true);
    try {
      const response = await jamChallengeDetailsApi.submitArena(eventName, challengeId);
      if (isStaleSelection(eventName, challengeId)) {
        return;
      }
      setArenaSubmitResult(response);
      // The judge is now running; seed a PENDING verdict so the panel starts polling immediately
      // instead of waiting a poll interval to learn there is a submission in flight.
      setArenaVerdict(
        JamArenaVerdict.fromPlainObject({ status: 'PENDING', submissionNumber: response.submissionNumber })
      );
    } finally {
      setIsSubmittingArena(false);
    }
  };

  const loadArenaVerdict = async (eventName: string, challengeId: string) => {
    const response = await jamChallengeDetailsApi.fetchArenaVerdict(eventName, challengeId);
    // Drop out-of-order responses for a no-longer-selected challenge.
    if (isStaleSelection(eventName, challengeId)) {
      return;
    }
    setArenaVerdict(response);
    // A SCORED verdict banks points; refresh the challenge so the header/score reflect it.
    if (response?.status === 'SCORED') {
      void refetchSelectedChallengeDetail();
    }
  };

  const loadJamChallengeDetails = async (params: { id: string; challengeId: string }): Promise<JamChallengeDetails> => {
    resetChallengeState();
    const { id, challengeId } = params;
    if (!id && !challengeId) {
      throw new Error('id and challengeId is required.');
    }
    updateSelectedChallengeKeys(id, challengeId);
    setChallengeModalMapFromStorage(id);
    try {
      const response = await jamChallengeDetailsApi.fetchJamChallengeDetailsData(id, challengeId);
      localStorageTTL.setItem(`challenge-details-${id}-${challengeId}`, response, 1, 'hours');
      setIsFetchingJamChallenge(false);
      updateSelectedJamChallenge(response);
      // The lab is only ready once an account is assigned AND the backend no longer reports
      // the challenge as awaiting lab assignment. This drives the existing challenge-status
      // banner ("Challenge Startup Underway...") for the waiting-for-lab state.
      const accountReady = !!response.awsLabAccount && !response.awaitingLabAssignment;
      toggleChallengeStatusModal(!accountReady, accountReady, challengeId);
      return response;
    } catch (e: any) {
      setIsFetchingJamChallenge(false);
      const errorCode = getErrorCode(e);
      if (errorCode === 710) {
        const previousData = localStorageTTL.getItem(`challenge-details-${id}-${challengeId}`) as
          | JamChallengeDetails
          | undefined;
        if (!!previousData?.id) {
          updateSelectedJamChallenge(previousData);
        }
        await loadJamChallengeProgressData({ id, challengeId });
        toggleChallengeStatusModal(true, false, challengeId);
      }
      throw e;
    }
  };

  useEffect(() => {
    setShowChallengeStatusProgress(false);
    setShowChallengeStatusSuccess(false);
  }, [selectedJamChallengeId]);

  const setChallengeModalMapFromStorage = (eventId: string) => {
    const challangeModalMapRaw: string | null = localStorage.getItem(eventId);
    if (challangeModalMapRaw) {
      const newChallengeModalMap: { [id: string]: boolean } = JSON.parse(challangeModalMapRaw) as {
        [id: string]: boolean;
      };
      setChallengeModalMap(newChallengeModalMap);
    }
  };

  const loadJamChallengeQueueInfo = async (eventName: string, challengeId: string) => {
    try {
      setQueueInfo(undefined);
      const response = await jamChallengeDetailsApi.getChallengeQueueInfo(eventName, challengeId);
      setQueueInfo(response);
    } catch (e) {
      // API handled error
    }
  };

  const loadJamChallengeTaskClue = async (
    params: { id: string; challengeId: string },
    taskId: string,
    clueOrder: number
  ): Promise<JamTaskClue> => {
    const { id, challengeId } = params;
    setIsFetchingTaskClue(true);

    try {
      const jamClue: JamTaskClue = await jamChallengeDetailsApi.fetchJamChallengeTaskClueData(
        id,
        challengeId,
        taskId,
        clueOrder
      );

      // Gate on the live selection (ref), not the closure task: a mid-flight task switch must not
      // let this clue revert the selection.
      const currentTask = selectedTaskRef.current;
      if (currentTask?.id === taskId && currentTask?.clues) {
        const filteredClueIndex = currentTask.clues.findIndex((clue: JamTaskClue) => clue.order === jamClue.order);
        if (filteredClueIndex !== -1) {
          const newClues = currentTask.clues;
          newClues[filteredClueIndex].description = jamClue.description;
          // if we want unlock clue button to
          // convert to show clue after above API call
          // we need to do `used = true` explicitly
          newClues[filteredClueIndex].used = true;
          updateTask({ ...currentTask, clues: newClues });
        }
      }
      return jamClue;
    } catch (e: any) {
      // If the error is 104 (clue already requested), the error message is the clue description.
      const currentTask = selectedTaskRef.current;
      if (getErrorCode(e) === 104 && currentTask?.id === taskId && currentTask?.clues) {
        const filteredClueIndex = currentTask.clues.findIndex((clue: JamTaskClue) => clue.order === clueOrder);
        if (filteredClueIndex !== -1) {
          const newClues = currentTask.clues;
          newClues[filteredClueIndex].description = getErrorMessage(e);
          newClues[filteredClueIndex].used = true;
          updateTask({ ...currentTask, clues: newClues });
        }
      }
      throw e;
    } finally {
      setIsFetchingTaskClue(false);
    }
  };

  const loadChallengeSupportDetails = async (eventName: string, challengeId: string) => {
    try {
      const response = await jamFacilitatorApi.getEventSupportDetails(safeString(eventName));
      setChallengeSupportDetails(response);
    } catch (e) {
      setIsFetchingJamChallenge(false);
      const errorCode = getErrorCode(e);
      if (errorCode === 710) {
        // TODO: move to errorcodes list
        toggleChallengeStatusModal(true, false, challengeId);
      }
      throw e;
    }
  };

  const startJamChallenge = async (params: { id: string; challengeId: string }) => {
    setIsStartingChallange(true);
    const { id, challengeId } = params;
    try {
      const response = await jamChallengeDetailsApi.startJamChallenge(id, challengeId);
      // Load the detail before the progress data. The progress load kicks off loadTeamProperties,
      // which decides whether to re-fetch the detail to unlock tasks - that decision needs the
      // post-start detail (awaitingLabAssignment) to already be in place.
      const details = await loadJamChallengeDetails({ id, challengeId });
      await loadJamChallengeProgressData({ id, challengeId });
      // Derive this from the response rather than selectedJamChallenge, which is still the
      // pre-start snapshot inside this closure.
      if (details?.awsLabAccount || details?.awaitingLabAssignment) {
        await loadTeamProperties(id, challengeId);
      }
      // hack: it takes a small amount to get the lab set and start the queue.
      // maybe we can use websocket. not sure though. need to check
      // for now load it here
      setTimeout(() => {
        void loadJamChallengeQueueInfo(id, challengeId);
      }, 1500);
      return response;
    } finally {
      setIsStartingChallange(false);
    }
  };

  const reStartJamChallenge = async (params: { id: string; challengeId: string; reason: string }) => {
    const { id, challengeId, reason } = params;
    // The restart can be triggered for a challenge that is NOT the one currently open in the
    // page (e.g. from the AI chat, which restarts pendingConfirmation.challengeId). Compute the
    // match up front: isStartingChallange drives the page-wide spinner / disabled controls, so
    // only toggle it when the restart is for the challenge actually on screen - otherwise an
    // out-of-page restart would put the unrelated challenge the user is viewing into a false
    // loading state until the API call resolves.
    const restartIsSelectedChallenge =
      id === selectedJamEventIdRef.current && challengeId === selectedJamChallengeIdRef.current;
    if (restartIsSelectedChallenge) {
      setIsStartingChallange(true);
    }
    try {
      await jamChallengeDetailsApi.restartJamChallenge(id, challengeId, reason);
      // The refresh steps below all key off the *selected* challenge refs:
      // refetchSelectedChallengeDetail early-returns on an id mismatch, and the
      // loadTeamProperties gate reads selectedJamChallengeRef. When the restarted challenge isn't
      // the one currently selected (a different challenge, or none) there is no matching in-page
      // UI to refresh, so skip the refresh entirely rather than refreshing the wrong challenge or
      // writing progress for a non-selected one. The restart API has already fired above.
      if (!restartIsSelectedChallenge) {
        return;
      }
      // A restart re-provisions the lab, so the detail goes back to the awaiting-lab state.
      // Refresh it before loading properties so task unlocking is driven by current data.
      await refetchSelectedChallengeDetail();
      await loadJamChallengeProgressData({ id, challengeId });
      if (selectedJamChallengeRef.current?.awsLabAccount || selectedJamChallengeRef.current?.awaitingLabAssignment) {
        await loadTeamProperties(id, challengeId);
      }
    } finally {
      if (restartIsSelectedChallenge) {
        setIsStartingChallange(false);
      }
    }
  };

  const toggleChallengeStatusModal = useCallback(
    (progress: boolean, success: boolean, challengeId: string) => {
      if (challengeId) {
        if (!challengeModalMap[challengeId]) setEnableChallengeStatusModal(true);
        setShowChallengeStatusProgress(progress);
        setShowChallengeStatusSuccess(success);
      }
    },
    [challengeModalMap]
  );

  const loadJamChallengeProgressData = useCallback(
    async (params: { id: string; challengeId: string }): Promise<JamProgress> => {
      setEnableChallengeStatusModal(false);
      const { id, challengeId } = params;
      const response = await jamChallengeDetailsApi.fetchJamChallengeProgress(id, challengeId);
      recordChallengeProgress(response);
      if (response?.started && (response?.hasAwsResources || response?.hasExternalResources)) {
        void loadTeamProperties(id, challengeId);
      } else if (response?.started && !response?.hasAwsResources && !response?.hasExternalResources) {
        // Handles no-lab case, as nothing needs to load
        void loadTeamProperties(id, challengeId, false);
      }
      return response;
    },
    [jamChallengeDetailsApi, toggleChallengeStatusModal]
  );

  const getJamChallengeTask = (params: { taskId: string }) => {
    const { taskId } = params;
    const challenge = selectedJamChallengeRef.current;
    const foundTask = challenge?.tasks?.find((taskItem) => taskItem.id === taskId);
    if (foundTask) {
      updateTask(foundTask);
    } else if (challenge && challenge.id === selectedJamChallengeIdRef.current) {
      updateTask(undefined);
    }
  };

  const validateTaskAnswer = async (
    params: { id: string; challengeId: string },
    answer: string,
    taskId: string | undefined
  ): Promise<boolean> => {
    const { id, challengeId } = params;
    setFetchingTaskValidationResponse(true);
    setTaskValidationResponse(undefined); // Clear previous response
    try {
      const response = await jamChallengeDetailsApi.validateTaskAnswer(id, challengeId, answer, taskId);
      setTaskValidationResponse(response);
      return response.taskCompleted;
    } finally {
      setFetchingTaskValidationResponse(false);
    }
  };

  const loadTeamProperties = async (eventName: string, challengeId: string, hasLab = true) => {
    try {
      const response = await jamChallengeDetailsApi.getTeamProperties(eventName, challengeId);
      // Drop out-of-order responses for a no-longer-selected challenge/event so a stale lab account isn't written back.
      if (isStaleSelection(eventName, challengeId)) {
        return;
      }
      setTeamPropertiesResponse(response);
      onTeamChallengePropertiesLoaded(response.properties || []);
      const labDeployed = !hasLab
        ? true
        : response?.properties?.length && response.properties.some((p) => p.key === 'team.accountNumber' && !!p.value);
      toggleChallengeStatusModal(!labDeployed, !!labDeployed, challengeId);
      // The team's lab account is now assigned. If our challenge detail still reflects the
      // "awaiting lab" state (tasks locked, content/clues stripped), re-fetch it so tasks unlock.
      // This reuses the existing lab-assignment polling rather than a dedicated websocket message.
      // Read the ref, not selectedJamChallenge: on the start-challenge path this runs from a
      // closure captured before the challenge was started, where awaitingLabAssignment is still
      // false, which previously meant the re-fetch never fired and tasks stayed locked until the
      // user reloaded the page.
      if (labDeployed && selectedJamChallengeRef.current?.awaitingLabAssignment) {
        void refetchSelectedChallengeDetail();
      }
    } catch (e: any) {
      const errorCode = getErrorCode(e);
      if (errorCode === 710) {
        // TODO: move to errorcodes list
        toggleChallengeStatusModal(true, false, challengeId);
      }
      throw e;
    }
  };

  const onTeamChallengePropertiesLoaded = (properties: KeyValuePair[]) => {
    setAwsAccount(undefined);

    const accountParams: string[] = [
      'team.accountNumber',
      'team.region',
      'team.url',
      'team.accessKey',
      'team.secretKey',
      'team.sessionToken',
      'team.token.expiration',
      'team.url.expiration',
    ];
    const propertiesMap: { [key: string]: number | string } = {
      'team.accountNumber': '',
      'team.region': '',
      'team.url': '',
      'team.accessKey': '',
      'team.secretKey': '',
      'team.sessionToken': '',
      'team.token.expiration': 0,
      'team.url.expiration': 0,
    };
    const codeWhisperKey = 'team.stackOutput.CodeWhispererCloud9URL';

    //* calculation of remaining properties
    const remainingProperties: KeyValuePair[] = [];
    for (const keyValue of properties) {
      if (!propertiesMap.hasOwnProperty(keyValue.key) && keyValue.key !== codeWhisperKey) {
        remainingProperties.push(keyValue);
      }
    }
    setOutputProperties(remainingProperties);

    // Build a fresh list and set it through state
    const nextAccountProperties: KeyValuePair[] = [];
    properties
      .filter((property) => property.key != null)
      .forEach(({ key, value, type, label, group }) => {
        if (accountParams.includes(key)) {
          propertiesMap[key] = value;
        } else {
          const strippedKey = key.replace('team.', '').replace('stackOutput.', '');
          if (strippedKey !== 'CodeWhispererCloud9URL') {
            nextAccountProperties.push({ key: strippedKey, value, type, label, group });
          }
        }
      });
    setAwsAccountProperties(nextAccountProperties);

    const accountNumber = propertiesMap['team.accountNumber'];

    if (accountNumber) {
      const AwsAccountInfo = new AwsAccount();
      AwsAccountInfo.accountNumber = accountNumber.toString();
      AwsAccountInfo.region = propertiesMap['team.region'].toString();
      AwsAccountInfo.url = propertiesMap['team.url'].toString();
      AwsAccountInfo.accessKey = propertiesMap['team.accessKey'].toString();
      AwsAccountInfo.secretKey = propertiesMap['team.secretKey'].toString();
      AwsAccountInfo.sessionToken = propertiesMap['team.sessionToken'].toString();
      AwsAccountInfo.expiration = Number(propertiesMap['team.token.expiration']);
      AwsAccountInfo.urlExpiration = Number(propertiesMap['team.url.expiration']);
      setAwsAccount(AwsAccountInfo);
    } else {
      setAwsAccount(undefined);
    }
  };

  const trackSolveWithButtonClick = async (
    eventName: string,
    challengeId: string,
    buttonKey: SolveChallengeButtonKey
  ) => {
    await jamChallengeDetailsApi.trackSolveButtonUsed(eventName, challengeId, buttonKey);
  };

  const loadTeamKeyPair = async (eventName: string, challengeId: string) => {
    setIsStartingChallange(true);
    await jamChallengeDetailsApi.getTeamKeyPair(eventName, challengeId);
    setIsStartingChallange(false);
  };

  const setEnableChallengeStatusModalFunc = () => {
    if (selectedJamChallengeId) {
      setEnableChallengeStatusModal(false);
      challengeModalMap[selectedJamChallengeId] = true;
      if (selectedJamEventId) localStorage.setItem(selectedJamEventId, JSON.stringify(challengeModalMap));
      setChallengeModalMap(challengeModalMap);
    }
  };

  // Confirm progress belongs to the selected challenge before consumers read it; undefined (loading) counts as no match.
  const progressMatchesSelectedChallenge =
    !!challengeProgress && !!selectedJamChallenge && challengeProgress.challengeId === selectedJamChallenge.id;
  const matchedChallengeProgress = progressMatchesSelectedChallenge ? challengeProgress : undefined;
  const isSelectedChallengeStarted = progressMatchesSelectedChallenge && !!challengeProgress?.started;

  const data: JamChallengeDetailsContextValue = {
    isFetchingJamChallengeDetails,
    loadJamChallengeTaskClue,
    loadJamChallengeDetails,
    selectedJamChallenge,
    progressMatchesSelectedChallenge,
    matchedChallengeProgress,
    isSelectedChallengeStarted,
    startJamChallenge,
    reStartJamChallenge,
    loadJamChallengeProgressData,
    validateTaskAnswer,
    challengeProgress,
    challengeRefreshTicks,
    progressById,
    challengeStatuses,
    task,
    awsAccount,
    awsAccountProperties,
    outputProperties,
    getJamChallengeTask,
    taskValidationResponse,
    loadTeamProperties,
    teamPropertiesResponse,
    trackSolveWithButtonClick,
    loadTeamKeyPair,
    jamChallengeId,
    isStartingChallange,
    showChallengeStatusProgress,
    showChallengeStatusSuccess,
    enableChallengeStatusModal,
    setEnableChallengeStatusModalFunc,
    isFetchingTaskClue,
    fetchingtaskValidationResponse,
    loadChallengeSupportDetails,
    challengeSupportDetails,
    loadJamChallengeQueueInfo,
    queueInfo,
    handleWSChallengeProgressUpdate: (messageBody: JamProgress) => {
      // The slot belongs to the selected challenge; another challenge's payload would flip
      // isSelectedChallengeStarted off, redirecting the task page and hiding the status banner.
      // Non-selected challenges refresh via the tick below.
      if (messageBody?.challengeId && messageBody.challengeId === selectedJamChallengeIdRef.current) {
        recordChallengeProgress(messageBody);
      }
      bumpChallengeRefreshTick(messageBody?.challengeId);
      // Fallback for the awaiting -> assigned transition if LabAssignmentWSMessage is missed.
      if (
        messageBody?.challengeId === selectedJamChallengeRef.current?.id &&
        !messageBody?.awaitingLabAssignment &&
        selectedJamChallengeRef.current?.awaitingLabAssignment
      ) {
        refreshSelectedChallengeLab();
      }
    },
    // Sent only after the assignment is persisted, so /progress already reflects it (no retry needed).
    handleWSLabAssignmentUpdate: ({ challengeId }) => {
      if (!challengeId) {
        return;
      }
      bumpChallengeRefreshTick(challengeId);
      if (challengeId === selectedJamChallengeIdRef.current) {
        refreshSelectedChallengeLab();
      }
    },
    // Keeps the selected challenge's labsAvailable live so the restart gate reflects pool changes.
    handleWSLabAvailabilityUpdate: ({ eventName, labAvailability }) => {
      const current = selectedJamChallengeRef.current;
      if (!current || !labAvailability || (eventName && eventName !== selectedJamEventIdRef.current)) {
        return;
      }
      const available = labAvailability[current.id];
      if (available === undefined || available === current.labsAvailable) {
        return;
      }
      updateSelectedJamChallenge(Object.assign(new JamChallengeDetails(), current, { labsAvailable: available }));
    },
    arenaWriteUpState,
    arenaSubmitResult,
    arenaVerdict,
    isSavingArenaWriteUp,
    isSubmittingArena,
    saveArenaWriteUp,
    submitArena,
    loadArenaVerdict,
  };
  return <JamChallengeDetailsContext.Provider value={data}>{children}</JamChallengeDetailsContext.Provider>;
};

const useJamChallengeDetails = () => {
  const context = useContext(JamChallengeDetailsContext);
  if (context === undefined) {
    throw new Error('useJamChallengeDetails can only be used inside JamChallengeDetailsProvider');
  }
  return context;
};

export { JamChallengeDetailsProvider, useJamChallengeDetails };
